import axios from 'axios';

// 配置默认axios实例
axios.defaults.baseURL = '/';
axios.defaults.timeout = 30000;

// 请求拦截器 - 自动添加token
axios.interceptors.request.use(
  (config) => {
    // 检查是否为管理后台请求
    const isAdminRequest = config.url?.startsWith('/api/admin');
    if (isAdminRequest) {
      const adminToken = localStorage.getItem('adminToken');
      if (adminToken) {
        config.headers.Authorization = `Bearer ${adminToken}`;
      }
    } else {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 不需要清除token的接口列表
// 这些接口返回 401 时不应该清除本地 token
const NO_TOKEN_CLEAR_PATHS = [
  '/api/admin/login',  // 后台登录：401 表示账号或密码错误，不是登录态失效
  '/api/users/login',
  '/api/users/login/code',
  '/api/users/send-code',
  '/api/users/send-login-code',
  '/api/users/verify-login',
  '/api/users/register',
  '/api/users/wechat/qrcode',
  '/api/users/wechat/scan-status',
  '/api/users/wechat/scan-callback',
  '/api/users/wechat/oauth',
  '/api/users/verify',     // token验证接口，401是正常的过期验证
  '/api/users/profile',    // 用户信息接口，401不一定要清除token
  '/api/chat/messages',    // 聊天消息接口，公开接口不需要清除token
  '/api/chat/send',        // 聊天发送接口，公开接口不需要清除token
  '/api/deepseek',         // AI对话接口，公开接口不需要清除token
  '/api/downloads',        // 文档下载接口，公开接口不需要清除token
  '/api/admin/service-hours' // 服务时段配置，公开接口不需要清除token
];

// 响应拦截器 - 处理 401 / 403
// 关键改进：只有在明确知道是无效token时才清除，避免误清除导致刷新后退出登录
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const status = error.response && error.response.status;
    // 403 一并处理：后端有两套鉴权中间件，同一个失效 token 会分别返回 401 和 403，
    // 只认 401 的话，走内联中间件的接口（用户/公告/服务记录/仪表盘）会一直卡在空列表
    if (status === 401 || status === 403) {
      const requestPath = error.config?.url || '';
      const isAdminRequest = requestPath.startsWith('/api/admin');

      // 后台请求用的是 adminToken，前台请求用 token；此前后台 401 一直按前台 token 判断，
      // 导致后台 token 失效时既不清除也不跳转，页面只剩「获取列表失败」的空列表
      const tokenKey = isAdminRequest ? 'adminToken' : 'token';
      const currentToken = localStorage.getItem(tokenKey);

      // 只有当本地确实有 token 时才考虑清除
      // 这样可以避免：本地没有 token，但某些公共接口误返回 401 时清除空值
      if (!currentToken) {
        return Promise.reject(error);
      }

      // 检查是否是应该清除 token 的接口
      const shouldClearToken = !NO_TOKEN_CLEAR_PATHS.some(path => requestPath.includes(path));

      if (shouldClearToken) {
        console.log('清除无效token:', tokenKey, requestPath);
        localStorage.removeItem(tokenKey);

        if (isAdminRequest) {
          // 后台登录态失效：回到后台登录页重新登录
          if (!window.location.pathname.startsWith('/admin/login')) {
            window.location.href = '/admin/login';
          }
          return Promise.reject(error);
        }

        localStorage.removeItem('user');

        // 前台 403 多为业务权限问题（如越权访问他人资源），不应踢到登录页；只有 401 才跳
        if (status !== 401) {
          return Promise.reject(error);
        }

        const isAdminPage = window.location.pathname.startsWith('/admin');
        const isWechatConfirmPage = window.location.pathname.startsWith('/wechat-confirm');
        const isWechatTestPage = window.location.pathname.startsWith('/wechat-test');
        const isLoginPage = window.location.pathname === '/login' || window.location.pathname === '/';

        if (!isAdminPage && !isWechatConfirmPage && !isWechatTestPage && !isLoginPage) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default axios;
